import React, { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  sfListScenarios, sfListRevenues, sfListVariableExpenses,
  sfCreateRevenue, sfPatchRevenue, sfDeleteRevenue,
} from '../../api/treasury';
import RevenuesTab from '../treasury/tabs/RevenuesTab';
import PageShell from '../../components/PageShell';
import Tabs from '../../components/Tabs';
import { resolveTabs } from '../../i18n/resolveTabs';
import { tp } from '../../i18n/useP';
import '../treasury/TreasuryPage.css';
import './Sales.css';

// Module Ventes → « Prévision des ventes ». Hub à onglets (PageShell framed +
// Tabs). L'onglet « Synthèse » porte le graphique (demi-page) avec une courbe de
// comparaison N-1 (facturation réelle) et un toggle CHF / kCHF. L'onglet « Plan
// des ventes » réutilise RevenuesTab (sans son graphe : il vit en Synthèse) sur
// le MÊME plan de référence que la Prévision trésorerie — données partagées.
const TAB_DEFS = [
  { id: 'sales-plan', labelKey: 'scenarii:forecast.tabs.salesPlan' },
];

export default function SalesForecastPage({ activeTenant, year, onYearChange }) {
  const { t } = useTranslation();
  const TABS = resolveTabs(TAB_DEFS, t);
  const tenantId = activeTenant?.id;
  const [activeTab, setActiveTab] = useState('sales-plan');
  const [scenario, setScenario] = useState(null);
  const [revenues, setRevenues] = useState([]);
  const [variables, setVariables] = useState([]);     // charges variables (coûts liés aux revenus)
  const [error, setError]       = useState(null);

  // Plan de référence (scénario base) — créé au premier accès s'il n'existe pas.
  useEffect(() => {
    if (!tenantId) { setScenario(null); setRevenues([]); return undefined; }
    let cancelled = false;
    (async () => {
      const r = await sfListScenarios(tenantId);
      if (cancelled) return;
      if (r.error) { setError(r.error); return; }
      let list = r.data || [];
      let base = list.find(s => s.type === 'base') || list[0];
      if (!base) {
        // Créer un scénario de liquidité est un acte Finance : le vendeur ne le
        // fait pas à son insu depuis son écran. On l'annonce plutôt.
        setError(tp('scenarii:forecast.noScenario'));
        return;
      }
      setScenario(base);
    })().catch(e => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [tenantId]);

  const refreshRevenues = useCallback(async () => {
    if (!tenantId || !scenario?.id) return;
    const r = await sfListRevenues(tenantId, scenario.id);
    if (!r.error) setRevenues(r.data || []);
  }, [tenantId, scenario?.id]);

  const refreshVariables = useCallback(async () => {
    if (!tenantId || !scenario?.id) { setVariables([]); return; }
    const r = await sfListVariableExpenses(tenantId, scenario.id);
    if (!r.error) setVariables(r.data || []);
  }, [tenantId, scenario?.id]);

  useEffect(() => { refreshRevenues(); }, [refreshRevenues]);
  useEffect(() => { refreshVariables(); }, [refreshVariables]);

  // Facturation réelle de l'année N-1 (= plan.start_year − 1) → courbe de
  // comparaison sur le graphe de synthèse. Source : facturation par catégorie
  // saisie dans Ventes → Paramètres ; on somme les catégories par mois.

  if (!tenantId) {
    return (
      <PageShell framed className="treasury sales-shell" title={t('scenarii:forecast.title')}>
        <div className="treasury__placeholder">{t('scenarii:forecast.selectTenant')}</div>
      </PageShell>
    );
  }

  return (
    <PageShell
      framed
      className="treasury"
      title={t('scenarii:forecast.title')}
    >
      <Tabs tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {error && (
        <div className="treasury__error">
          {error} <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {activeTab === 'sales-plan' && scenario && (
        <RevenuesTab
          tenantId={tenantId}
          scenarioId={scenario.id}
          scenario={scenario}
          locked={scenario.locked}
          revenues={revenues}
          api={{
            createRevenue: sfCreateRevenue,
            updateRevenue: sfPatchRevenue,
            deleteRevenue: sfDeleteRevenue,
          }}
          variables={variables}
          onChange={refreshRevenues}
          onChangeVariables={refreshVariables}
          showChart={false}
          hideTitle
        />
      )}

    </PageShell>
  );
}
